/**
 * Renders one template section against a draft: visible items only (live
 * show/hide rules), fields, controls and layout blocks, wired to the draft
 * service. Kept free of routing so it can be tested in isolation.
 */
import { isItemVisible, localize, type ControlAnswer, type ControlValue, type LayoutItem, type Section, type SurveyContext, type Template, type ValidationIssue } from '@sss/form-engine';
import { Banner } from '../../components';
import { useI18n } from '../../lib/i18n';
import type { CarriedFinding } from '../../lib/api/types';
import type { DraftRecord } from '../../lib/offline/types';
import { ControlCard } from './ControlCard';
import { canEditItem } from './draft-service';
import { FieldRenderer } from './FieldRenderer';
import type { CaptureActions, CaptureLookup } from './types';

export interface SectionFormProps {
  template: Template;
  section: Section;
  draft: DraftRecord;
  context: SurveyContext;
  lang: string;
  actions: CaptureActions;
  lookup: CaptureLookup;
  setField: (key: string, value: unknown) => void;
  setControl: (code: string, patch: Partial<ControlAnswer>) => void;
  /** Validation issues to display (after a submit attempt). */
  issues?: ValidationIssue[];
  carried?: CarriedFinding[];
  /** Control numbering offset (questions are numbered across the survey). */
  numberFrom?: number;
  /** RETURNED survey: refs editable besides the flagged ones (items the flagged answers reveal). */
  editable?: ReadonlySet<string> | null;
}

function LayoutBlock({ item, lang, src }: { item: LayoutItem; lang: string; src: string }) {
  const text = localize(item.text, lang, src);
  if (item.type === 'heading') return <h3 className="mt-2 text-lg font-bold">{text}</h3>;
  return (
    <Banner tone={item.type === 'warning' ? 'warning' : 'info'}>
      <span className="whitespace-pre-line">{text}</span>
    </Banner>
  );
}

export function SectionForm(p: SectionFormProps) {
  const { template, section, draft, context, lang } = p;
  const { t } = useI18n();
  const src = template.sourceLanguage;
  const scope = { answers: draft.answers, context };
  const issuesFor = (ref: string) => (p.issues ?? []).filter((i) => i.ref === ref).map((i) => i.code);
  let n = p.numberFrom ?? 0;

  const visible = section.items.filter((it) => isItemVisible(section, it, scope));
  if (visible.length === 0) return <p className="text-muted">{t('ui.capture.sectionEmpty')}</p>;

  return (
    <div className="flex flex-col gap-4">
      {section.description && <p className="text-muted">{localize(section.description, lang, src)}</p>}
      {visible.map((item) => {
        if (item.kind === 'layout') return <LayoutBlock key={item.id} item={item} lang={lang} src={src} />;
        if (item.kind === 'field') {
          const editable = canEditItem(draft, item.key, p.editable);
          const errs = issuesFor(item.key);
          return (
            <div key={item.id} className="rounded-[var(--radius)] border border-border bg-surface p-4 shadow-card">
              <FieldRenderer
                field={item}
                value={draft.answers.fields[item.key]}
                fields={draft.answers.fields}
                onChange={(v) => p.setField(item.key, v)}
                lang={lang}
                sourceLanguage={src}
                disabled={!editable}
                error={errs.length ? t(`ui.validation.${errs[0]}`) : null}
                actions={p.actions}
                lookup={p.lookup}
                flagNote={draft.flags[item.key]}
              />
            </div>
          );
        }
        n += 1;
        return (
          <ControlCard
            key={item.id}
            number={n}
            control={item}
            answer={draft.answers.controls[item.code]}
            lang={lang}
            sourceLanguage={src}
            actions={p.actions}
            disabled={!canEditItem(draft, item.code, p.editable)}
            errors={issuesFor(item.code)}
            flagNote={draft.flags[item.code]}
            carried={p.carried?.find((f) => f.controlCode === item.code)}
            siteId={draft.siteId}
            surveyId={draft.id}
            onAnswer={(value) => p.setControl(item.code, { value, baselineVerified: false })}
            onNote={(note) => p.setControl(item.code, { note })}
            onPatch={(patch) => p.setControl(item.code, patch)}
          />
        );
      })}
    </div>
  );
}
